'use client';
import React, { useState, useEffect } from 'react';

interface NavItem {
  label: string;
  items: string[];
}

const navItems: NavItem[] = [
  {
    label: 'Shop Plans',
    items: ['Consonantal Core', 'Consonantal Plus', 'Consonantal Premium', 'Consonantal Elite', 'Compare Plans', 'Day Passes'],
  },
  {
    label: 'Browse Channels',
    items: ['Live News', 'Sports Channels', 'Entertainment', 'Premium Add-ons', 'Local Channels', 'International'],
  },
  {
    label: 'Explore Sports',
    items: ['Football', 'Basketball', 'Baseball', 'Soccer', 'Tennis', 'Combat Sports', 'Schedules'],
  },
  {
    label: 'Get Deals',
    items: ['Current Promos', 'Prepay & Save', 'Gift Cards', 'Free Streaming'],
  },
];

export default function Navbar() {
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#171725]/95 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.5)]' : 'bg-[#171725]/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-[68px]">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-1">
              <div className="flex gap-[3px] items-end">
                {[4, 7, 10, 7, 4].map((h, i) => (
                  <div
                    key={i}
                    className="w-[3px] rounded-full bg-white"
                    style={{ height: `${h}px` }}
                  />
                ))}
              </div>
              <span className="text-white font-black text-xl ml-1 tracking-tight">sling</span>
            </div>
            <span className="text-[#00b9ff] font-black text-[11px] uppercase tracking-widest leading-tight hidden sm:block">
              Consonantal Digital
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item, idx) => (
              <div
                key={idx}
                className="relative"
                onMouseEnter={() => setActiveDropdown(idx)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button className="nav-link">
                  {item.label}
                  <svg className={`w-3 h-3 transition-transform duration-200 ${activeDropdown === idx ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {activeDropdown === idx && (
                  <div className="absolute top-full left-0 pt-2 z-50">
                    <div className="dropdown-menu">
                      {item.items.map((subItem, sIdx) => (
                        <a key={sIdx} href="#" className="dropdown-item">
                          {subItem}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            <a
              href="tel:8888182608"
              className="hidden md:flex items-center gap-1.5 text-[#00b9ff] hover:text-white transition-colors duration-200 text-sm font-bold"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              888-818-2608
            </a>
            <a href="#" className="hidden sm:flex items-center gap-2 text-white hover:text-[#00b9ff] transition-colors duration-200">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-sm font-700 hidden md:block">Sign In</span>
            </a>
            <a href="#plans" className="btn-primary text-sm px-5 py-2.5">
              Sign Up
            </a>
            {/* Mobile menu button */}
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#20222f] border-t border-white/10 px-4 py-4">
          {navItems.map((item, idx) => (
            <div key={idx} className="mb-4">
              <div className="text-white font-bold text-sm mb-2">{item.label}</div>
              <div className="grid grid-cols-2 gap-1">
                {item.items.map((subItem, sIdx) => (
                  <a key={sIdx} href="#" className="text-[#979797] text-sm py-1 hover:text-[#00b9ff] transition-colors">
                    {subItem}
                  </a>
                ))}
              </div>
            </div>
          ))}
          <div className="pt-4 border-t border-white/10 flex gap-3">
            <a href="#" className="btn-secondary text-sm flex-1 text-center">Sign In</a>
            <a href="#plans" className="btn-primary text-sm flex-1 text-center">Sign Up</a>
          </div>
        </div>
      )}
    </header>
  );
}
